import { ArrowRight, BookOpen } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import { localizedPurposeCopy } from "../../lib/i18n/intakeWizard";
import type { Pathway } from "../../lib/learning/types";

export function ContinueLearningCard({
  plan,
  progressPercent,
  nextUp,
  onContinue,
  accent = "violet",
}: {
  plan: Pathway;
  progressPercent: number | null;
  nextUp: string | null;
  onContinue: () => void;
  accent?: "violet" | "green";
}) {
  const { strings, locale } = useLearnLocale();
  const pct =
    progressPercent != null ? Math.min(100, Math.max(0, Math.round(progressPercent))) : null;

  // Without an explicit name the button concatenates every descendant, so the
  // Chrome a11y tree on stage read the whole card as one run-on string ending
  // in a bare "0" picked up from the nested progressbar's value.
  const label =
    pct != null
      ? `${strings.continueLabel} — ${strings.planProgressAria(plan.title, pct)}`
      : `${strings.continueLabel} — ${plan.title}`;

  return (
    <button
      type="button"
      onClick={onContinue}
      aria-label={nextUp ? `${label} — ${strings.nextUp(nextUp)}` : label}
      className={cn(
        "hero-orbs group relative w-full overflow-hidden rounded-[1.5rem] p-5 text-left text-white shadow-[var(--shadow-float)] transition-transform duration-150 ease-out active:scale-[0.99] motion-reduce:transition-none sm:p-6",
        accent === "green" ? "hero-parent" : "hero-celebrate",
      )}
    >
      <div className="relative z-10 space-y-4">
        <div className="flex items-start gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15 backdrop-blur-sm">
            <BookOpen className="size-6" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display truncate text-lg font-semibold leading-tight">
                {plan.title}
              </p>
              <span className="rounded-full bg-emerald-400/90 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-emerald-950">
                {strings.pathwayActive}
              </span>
            </div>
            <p className="mt-1 text-sm text-white/85">
              {localizedPurposeCopy(locale, plan.purpose).label}
            </p>
          </div>
        </div>

        {pct != null ? (
          <div className="flex items-center gap-3">
            <PlanProgressRing percent={pct} label={strings.planProgressAria(plan.title, pct)} />
            <p className="text-sm text-white/90">
              {nextUp ? strings.nextUp(nextUp) : strings.yourProgress}
            </p>
          </div>
        ) : (
          <p className="text-sm text-white/85">
            {nextUp ? strings.nextUp(nextUp) : strings.tapToOpenPlan}
          </p>
        )}

        <div className="flex justify-end">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[color-mix(in_oklch,var(--color-primary)_70%,black)] transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transform-none">
            {strings.continueLabel}
            <ArrowRight className="size-4" aria-hidden="true" />
          </span>
        </div>
      </div>
    </button>
  );
}

function PlanProgressRing({ percent, label }: { percent: number; label: string }) {
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;
  return (
    <svg
      viewBox="0 0 36 36"
      className="size-14 shrink-0 text-white"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.25}
        strokeWidth="3"
      />
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeDasharray={`${filled} ${circumference}`}
        strokeLinecap="round"
        transform="rotate(-90 18 18)"
      />
      <text
        x="18"
        y="19"
        textAnchor="middle"
        fill="currentColor"
        fontSize="7"
        fontWeight="600"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {percent}%
      </text>
    </svg>
  );
}
